import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectTarget = searchParams.get('redirect') === 'digitize' ? '/digitize/upload' : '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate(redirectTarget);
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden" style={{ backgroundColor: '#0F172A' }}>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1747831338811-8773622b064b?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.85) 100%)' }} />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="mb-10">
            <img
              src="/images/fmiq-one-logo.png"
              alt="FMiQ One"
              className="h-24 w-auto mb-8"
            />
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tight text-white mb-4">
            Construction Intelligence Platform
          </h1>
          <p className="text-slate-400 leading-relaxed">
            Transform construction drawings into structured building models.
            Extract room schedules, equipment data, and floor plan intelligence
            with AI-powered precision.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4">
            {[
              { label: 'PDF & Image', desc: 'Ingest' },
              { label: 'AI-Powered', desc: 'Extraction' },
              { label: 'Human Review', desc: 'Workflow' },
              { label: 'FMiQ-One', desc: 'Sync' },
            ].map((item, i) => (
              <div key={i} className="border border-slate-700/50 bg-slate-800/30 p-4 rounded-md">
                <p className="font-heading font-bold text-xs uppercase tracking-wider text-blue-400">{item.desc}</p>
                <p className="font-mono text-sm text-slate-300 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <img src="/images/fmiq-one-logo.png" alt="FMiQ One" className="h-8 w-auto" />
          </div>

          <h2 className="font-heading font-bold text-2xl tracking-tight text-foreground mb-1">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            {isRegister ? 'Set up your workspace' : 'Access your projects'}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 text-rose-600 dark:text-rose-400 text-xs font-mono rounded-md" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                  className="w-full bg-card border border-border focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 rounded-md px-3 py-2.5 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="Your name"
                  required
                />
              </div>
            )}
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-card border border-border focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 rounded-md px-3 py-2.5 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-card border border-border focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 rounded-md px-3 py-2.5 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                placeholder="Enter password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 rounded-md transition-colors disabled:opacity-50 mt-2 shadow-sm"
            >
              {loading ? 'Processing...' : isRegister ? 'Create Account' : 'Sign In'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              data-testid="toggle-auth-mode"
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors font-medium"
            >
              {isRegister ? 'Sign In' : 'Create one'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
